// Service hook (TS-2.5.01, Resource Accessor layer) - the only place the decks feature's version
// list screen calls publicationApi.listVersions. Mirrors useProjects/useRoadmap's shape: fetch on
// mount and whenever the caller's memoized params change, refetch on any push-channel
// invalidation for a DeckVersion write, surface a typed ApiError rather than throwing.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import type { DeckVersionSummary, PagedResult } from "../../../api/generated/contracts";
import type { ListVersionsParams } from "../../../api/publication";
import { publicationApi } from "../../../api/publication";
import { useInvalidation } from "../../../realtime/useInvalidation";

export interface UseDeckVersionsResult {
  readonly page: PagedResult<DeckVersionSummary> | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly refresh: () => void;
}

/** `params` must be caller-memoized, the same contract useProjects places on its own filter argument. */
export function useDeckVersions(params: ListVersionsParams): UseDeckVersionsResult {
  const [page, setPage] = useState<PagedResult<DeckVersionSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    void publicationApi
      .listVersions(params)
      .then((next) => {
        setPage(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The version list could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh, "DeckVersion");

  return { page, loading, error, refresh };
}
